import { PageAdsApi, PageAdsProps, PluginType } from '@accedo/one-plugin-sdk';
import { ErrorBoundary } from '@dynamic/ui';
import { createRef, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { useGlobalContext } from '#/components/useGlobalContext';
import { useConfig } from '#/hooks';
import { getCcpaOutOutSetting } from '#/service/user/settings';

import pluginManager from '../pluginManager';

const onError = (error: Error, stack: string) => {
  console.error('PageAds plugin error', error, stack);
};

interface PageAdsContainerProps {
  $isVisible: boolean;
}

const PageAdsContainer = styled.div<PageAdsContainerProps>`
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  z-index: 1;
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};

  /* make sure that at least the container won't block clicks */
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

// common props that any page ads plugin can use
interface CommonProps {
  routeFilter?: string;
}

const PageAdsManager = () => {
  const {
    boot: { isPluginManagerReady },
  } = useGlobalContext();
  const {
    features: {
      pageAds: [firstConfig],
    },
  } = useConfig();
  const { pathname } = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  const ccpaOptOut = getCcpaOutOutSetting();

  // common props that applies to all plugins
  const { routeFilter = '' } = (firstConfig?.properties as CommonProps) || {};

  // regex to match routes where we should show ads
  const routeFilterRegex = useMemo(
    () => new RegExp(routeFilter, 'i'),
    [routeFilter]
  );

  // show/hide on route change using the configured routeFilter
  useEffect(() => {
    const match = !!pathname.match(routeFilterRegex);
    setIsVisible(match);
  }, [pathname, routeFilterRegex]);

  // wrapped component rendered by the plugin
  const pageAdsComponent = useMemo(() => {
    if (!firstConfig || !isPluginManagerReady) {
      return null;
    }

    const PageAdsComponent = pluginManager.getPluginComponent<PageAdsProps>({
      type: PluginType.PageAds,
      id: firstConfig.plugin?.id,
      optional: true,
    });
    const apiRef = createRef<PageAdsApi>();

    return (
      <ErrorBoundary key={firstConfig.key} onError={onError}>
        <Suspense fallback={null}>
          <PageAdsComponent apiRef={apiRef} ccpaOptOut={ccpaOptOut} />
        </Suspense>
      </ErrorBoundary>
    );
  }, [ccpaOptOut, firstConfig, isPluginManagerReady]);

  if (!pageAdsComponent) {
    return null;
  }

  return (
    <PageAdsContainer $isVisible={isVisible}>
      {pageAdsComponent}
    </PageAdsContainer>
  );
};

export default PageAdsManager;
